import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import {
  queryKeys,
  loadSkyspaceProfileTheme,
  searchBlueskyUsersPage,
  searchSkyspacePeoplePage,
} from '../../data/queries';
import {
  isSupporter as isSkyspaceSupporter,
  loadSupporters,
} from '../../data/supporters';
import { profileSearch } from '../../lib/route';
import { ErrorState, LoadingState } from '../../ui/AsyncState';
import { visibleActors } from '../../domain/moderation';
import { useSettings } from '../../settings/SettingsContext';
import { useAuth } from '../../auth/AuthContext';
import { getSiteModeration } from '../../data/site-services';
import {
  classicPanelColour,
  normalizeClassicTheme,
  type ClassicTheme,
} from '../../domain/classic-profile';
import type { ActorProfile } from '../../domain/types';
import {
  backgroundAssetUrl,
  cssFontStack,
  ensureGoogleFontLink,
  profileCardBackground,
} from '../profile/profile-theme';

const ShaderCardPreview = lazy(() => import('../profile/ShaderCardPreview'));

const searchCardPanelLayout: CSSProperties = {
  display: 'grid',
  gridTemplateRows: 'minmax(0, 1fr) auto',
  alignItems: 'stretch',
};

const searchCardNameSlot: CSSProperties = {
  position: 'static',
  inset: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0,
  overflow: 'hidden',
  transform: 'none',
};

const searchCardNameText: CSSProperties = {
  display: '-webkit-box',
  maxHeight: '2.2em',
  overflow: 'hidden',
  lineHeight: 1.1,
  overflowWrap: 'anywhere',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};

const searchCardHandle: CSSProperties = {
  position: 'static',
  inset: 'auto',
  display: 'block',
  width: '100%',
  maxHeight: '2.1em',
  overflow: 'hidden',
  lineHeight: 1.05,
  overflowWrap: 'normal',
  wordBreak: 'normal',
};

const searchCardHandleSegment: CSSProperties = {
  whiteSpace: 'nowrap',
};

function ProfileHandle({ handle }: { handle: string }) {
  const firstDot = handle.indexOf('.');
  const name = firstDot < 0 ? handle : handle.slice(0, firstDot);
  const suffix = firstDot < 0 ? '' : handle.slice(firstDot);
  return (
    <>
      <span style={searchCardHandleSegment}>@{name}</span>
      {suffix && (
        <>
          <wbr />
          <span style={searchCardHandleSegment}>{suffix}</span>
        </>
      )}
    </>
  );
}

function supporterAnimationDelay(person: ActorProfile): string {
  const identity = person.did || person.handle;
  let hash = 0;
  for (let index = 0; index < identity.length; index += 1) {
    hash = ((hash * 31) + identity.charCodeAt(index)) >>> 0;
  }
  return `-${hash % 6800}ms`;
}

function useNearViewport() {
  const ref = useRef<HTMLLIElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  useEffect(() => {
    if (!ref.current || nearViewport) return undefined;
    if (!('IntersectionObserver' in window)) {
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [nearViewport]);
  return { ref, nearViewport };
}

export function SearchProfileCard({
  person,
  supporter,
  pathname,
  themed,
  reason,
  compact = false,
  appearance,
}: {
  person: ActorProfile;
  supporter: boolean;
  pathname: string;
  themed: boolean;
  reason?: string;
  compact?: boolean;
  appearance?: ClassicTheme;
}) {
  const history = useHistory();
  const { ref, nearViewport } = useNearViewport();
  const appearanceQuery = useQuery({
    queryKey: queryKeys.profileAppearance(person.did),
    queryFn: ({ signal }) => loadSkyspaceProfileTheme(person, signal),
    enabled: themed && nearViewport && !appearance,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const theme = appearance || appearanceQuery.data || normalizeClassicTheme(undefined);
  const hasTheme = Boolean(appearance || appearanceQuery.data);
  const cardStyle = {
    ...(hasTheme ? {
      ...profileCardBackground(theme),
      color: theme.bodyColor,
      fontFamily: cssFontStack(theme.font),
    } : {}),
    ...(supporter ? {
      '--supporter-delay': supporterAnimationDelay(person),
    } : {}),
    cursor: 'pointer',
  } as CSSProperties;
  useEffect(() => {
    if (appearanceQuery.data) ensureGoogleFontLink(theme.font);
  }, [appearanceQuery.data, theme.font]);

  return (
    <li
      ref={ref}
      className={`profile-card${compact ? ' compact-profile-card' : ''}${supporter ? ' gold' : ''}`}
      style={cardStyle}
      data-profile-themed={hasTheme ? 'true' : 'false'}
      key={person.did || person.handle}
      role="link"
      tabIndex={0}
      aria-label={`Open ${person.displayName || `@${person.handle}`}'s profile`}
      onClick={() => history.push({
        pathname,
        search: profileSearch(person.handle),
      })}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          history.push({
            pathname,
            search: profileSearch(person.handle),
          });
        }
      }}
    >
      {hasTheme && theme.backgroundKind === 'video' && theme.backgroundUrl && (
        <video
          className="modern-profile-card-video"
          src={backgroundAssetUrl(theme.backgroundUrl)}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
      )}
      {hasTheme && theme.backgroundKind === 'shader' && theme.shader && (
        <Suspense fallback={null}>
          <ShaderCardPreview shader={theme.shader} theme={theme} />
        </Suspense>
      )}
      <img
        src={person.avatar || '../placeholder.png'}
        alt=""
        width="100"
        height="100"
        loading="lazy"
      />
      <div
        className="profile-name-container"
        style={{
          ...searchCardPanelLayout,
          ...(hasTheme ? {
            backgroundColor: classicPanelColour(theme),
            border: `${Math.min(theme.frameWidth, 5)}px ${theme.frameStyle} ${theme.frameColor}`,
            borderRadius: `${Math.min(theme.frameRadius, 16)}px`,
            color: theme.bodyColor,
          } : {}),
        }}
      >
        <span
          className="profile-name"
          style={{ ...searchCardNameSlot, ...(hasTheme ? { color: theme.bodyColor } : {}) }}
        >
          <span style={searchCardNameText}>{person.displayName || person.handle}</span>
        </span>
        {!compact && (
          <span
            className="profile-location"
            style={{ ...searchCardHandle, ...(hasTheme ? { color: theme.bodyColor } : {}) }}
          >
            <ProfileHandle handle={person.handle} />
          </span>
        )}
      </div>
      {reason && <p className="skyspace-profile-card-reason">{reason}</p>}
    </li>
  );
}

export function SearchPage({
  query,
  onlyInterests,
  searchType,
}: {
  query: string;
  onlyInterests: boolean;
  searchType: 'skyspace' | 'users';
}) {
  const location = useLocation();
  const auth = useAuth();
  const { settings } = useSettings();
  const peopleQuery = useInfiniteQuery({
    queryKey: queryKeys.search(query, onlyInterests, searchType),
    queryFn: ({ pageParam, signal }) => searchType === 'users'
      ? searchBlueskyUsersPage(query, pageParam, signal)
      : searchSkyspacePeoplePage(query, onlyInterests, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.cursor,
  });
  const supportersQuery = useQuery({
    queryKey: queryKeys.supporters,
    queryFn: ({ signal }) => loadSupporters(signal),
  });
  const moderationQuery = useQuery({
    queryKey: queryKeys.siteModeration,
    queryFn: ({ signal }) => getSiteModeration(signal),
    staleTime: 30_000,
  });
  const supporters = supportersQuery.data || [];
  const blockedHandles = new Set(moderationQuery.data?.blockedUsers || []);
  const people = [...new Map(
    visibleActors(
      peopleQuery.data?.pages.flatMap(page => page.people) || [],
      blockedHandles,
      settings.showSensitiveContent,
      Boolean(auth.did),
    )
      .map(person => [person.handle, person]),
  ).values()];

  if (peopleQuery.isPending) {
    return <LoadingState label={query ? `Searching for “${query}”` : 'Finding the latest people'} />;
  }
  if (peopleQuery.isError) {
    return <ErrorState title="Search is temporarily unavailable" error={peopleQuery.error} />;
  }

  return (
    <section className="search-results-container" data-testid="modern-search-results">
      <h1>
        {query
          ? `${searchType === 'users' ? 'Bluesky users' : 'SkySpace results'} for “${query}”`
          : 'Latest People!'}
      </h1>
      {!query && (
        <p>People who have recently signed in or done something public on SkySpace.</p>
      )}
      {searchType === 'users' && <p>Searching Bluesky display names and handles.</p>}
      {searchType === 'skyspace' && onlyInterests && <p>Searching profile interests.</p>}

      {people.length === 0 ? (
        <div className="classic-state-panel">
          <h2>No matches yet</h2>
          <p>Try a broader name, handle, place, or interest.</p>
        </div>
      ) : (
        <ul className="profiles-grid classic-search-grid">
          {people.map(person => (
            <SearchProfileCard
              person={person}
              supporter={isSkyspaceSupporter(supporters, person)}
              pathname={location.pathname}
              themed={searchType === 'skyspace'}
              key={person.did || person.handle}
            />
          ))}
        </ul>
      )}
      {peopleQuery.hasNextPage && (
        <div id="view-more-link">
          <button
            type="button"
            className="classic-text-button"
            disabled={peopleQuery.isFetchingNextPage}
            onClick={() => void peopleQuery.fetchNextPage()}
          >
            {peopleQuery.isFetchingNextPage ? 'Loading more…' : 'View more people'}
          </button>
        </div>
      )}
    </section>
  );
}
